<script setup>
import { ref, watch, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import EmojiInput from 'shared/components/emoji/EmojiInput.vue';
import SlotsConfigAPI from 'dashboard/api/omni/slotsConfig';
import TemplatePickersToolbar from './TemplatePickers/TemplatePickersToolbar.vue';
import ApiAutomationsPanel from './ApiAutomations/ApiAutomationsPanel.vue';

const MAX_FILE_SIZE = 40 * 1024 * 1024; // 40MB
const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'video/mp4',
];

const { t } = useI18n();

const props = defineProps({
  slot: {
    type: Object,
    default: null,
  },
  isOpen: {
    type: Boolean,
    default: false,
  },
  bots: {
    type: Array,
    default: () => [],
  },
  selectedBotId: {
    type: Number,
    default: null,
  },
  calendars: {
    type: Array,
    default: () => [],
  },
  catalogs: {
    type: Array,
    default: () => [],
  },
  allSlots: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['save', 'close', 'remove-file']);

const form = ref({
  agent_bot_id: null,
  slot_name: '',
  slot_description: '',
  question: '',
  priority: '',
  restriction: '',
  response_instruction: '',
  action_type: 'ask_question',
  ai_temperature: 'rigid',
  action_config: {},
});

// File upload state
const selectedFile = ref(null);
const fileError = ref('');
const fileInputRef = ref(null);

// Flag to prevent action_type watcher from clearing fields during form reset
const isResettingForm = ref(false);

// State for JSON preview
const showJsonPreview = ref(false);

// State for emoji picker
const showEmojiPicker = ref(false);
const questionInput = ref(null);

// Action type options (alphabetical by pt_BR label)
const actionTypeOptions = [
  { value: 'api_call', label: 'OMNI.SLOTS.ACTION_TYPES.API_CALL', icon: 'i-lucide-globe' },
  { value: 'query_database', label: 'OMNI.SLOTS.ACTION_TYPES.QUERY_DATABASE', icon: 'i-lucide-database' },
  { value: 'check_calendar', label: 'OMNI.SLOTS.ACTION_TYPES.CHECK_CALENDAR', icon: 'i-lucide-calendar' },
  { value: 'consult_catalog', label: 'OMNI.SLOTS.ACTION_TYPES.CONSULT_CATALOG', icon: 'i-lucide-shopping-bag' },
  { value: 'send_file', label: 'OMNI.SLOTS.ACTION_TYPES.SEND_FILE', icon: 'i-lucide-paperclip' },
  { value: 'ask_question', label: 'OMNI.SLOTS.ACTION_TYPES.ASK_QUESTION', icon: 'i-lucide-message-circle' },
  { value: 'decision', label: 'OMNI.SLOTS.ACTION_TYPES.DECISION', icon: 'i-lucide-brain' },
  { value: 'free', label: 'OMNI.SLOTS.ACTION_TYPES.FREE', icon: 'i-lucide-infinity' },
  { value: 'loop', label: 'OMNI.SLOTS.ACTION_TYPES.LOOP', icon: 'i-lucide-repeat' },
];

// AI Temperature options
const aiTemperatureOptions = [
  { value: 'rigid', label: 'OMNI.SLOTS.AI_TEMPERATURE_OPTIONS.RIGID' },
  { value: 'flexible', label: 'OMNI.SLOTS.AI_TEMPERATURE_OPTIONS.FLEXIBLE' },
  { value: 'creative', label: 'OMNI.SLOTS.AI_TEMPERATURE_OPTIONS.CREATIVE' },
];

// Computed: is the current action type ask_question?
const isAskQuestion = computed(() => form.value.action_type === 'ask_question');
const isCheckCalendar = computed(() => form.value.action_type === 'check_calendar');
const isQueryDatabase = computed(() => form.value.action_type === 'query_database');
const isSendFile = computed(() => form.value.action_type === 'send_file');
const isDecision = computed(() => form.value.action_type === 'decision');
const isFree = computed(() => form.value.action_type === 'free');
const isLoop = computed(() => form.value.action_type === 'loop');
const isConsultCatalog = computed(() => form.value.action_type === 'consult_catalog');
const isApiCall = computed(() => form.value.action_type === 'api_call');

// api_call embeds the response-automations panel (conditions + actions), which
// needs a wider canvas than the other action types to avoid content overflowing.
const modalSizeClass = computed(() => (isApiCall.value ? 'max-w-3xl' : 'max-w-lg'));

// API Call test state
const apiTestLoading = ref(false);
const apiTestResult = ref(null);
const apiTestError = ref('');
const apiBodyInput = ref(null);

const apiMethodOptions = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

const resetApiTestState = () => {
  apiTestLoading.value = false;
  apiTestResult.value = null;
  apiTestError.value = '';
};

const insertAiPlaceholder = () => {
  const input = apiBodyInput.value;
  const placeholder = '{preenchido_por_IA}';
  const currentBody = form.value.action_config.body || '';

  if (!input) {
    form.value.action_config.body = currentBody + placeholder;
    return;
  }

  const start = input.selectionStart;
  const end = input.selectionEnd;

  form.value.action_config.body =
    currentBody.slice(0, start) + placeholder + currentBody.slice(end);

  setTimeout(() => {
    input.focus();
    const pos = start + placeholder.length;
    input.setSelectionRange(pos, pos);
  }, 0);
};

const insertTokenAtCursor = (refInput, configKey, token) => {
  const input = refInput?.value;
  const current = form.value.action_config[configKey] || '';
  if (!input) {
    form.value.action_config[configKey] = current + token;
    return;
  }
  const start = input.selectionStart ?? current.length;
  const end = input.selectionEnd ?? current.length;
  form.value.action_config[configKey] =
    current.slice(0, start) + token + current.slice(end);
  setTimeout(() => {
    input.focus();
    const pos = start + token.length;
    input.setSelectionRange(pos, pos);
  }, 0);
};

const apiUrlInput = ref(null);
const apiHeadersInput = ref(null);

const currentSlotId = computed(() => props.slot?.id || null);
const allSlotsList = computed(() => props.allSlots || []);
const apiResponseSample = computed(() => form.value.action_config?.test_response_sample ?? null);

const handleUrlPick = payload => insertTokenAtCursor(apiUrlInput, 'url', payload.token);
const handleHeadersPick = payload => insertTokenAtCursor(apiHeadersInput, 'headers', payload.token);
const handleBodyPick = payload => insertTokenAtCursor(apiBodyInput, 'body', payload.token);

const normalizeUrl = rawUrl => {
  const trimmed = (rawUrl || '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const handleApiTest = async () => {
  const rawUrl = form.value.action_config.url;
  if (!rawUrl) {
    apiTestError.value = t('OMNI.SLOTS.API_CALL.URL_REQUIRED');
    return;
  }

  const url = normalizeUrl(rawUrl);
  form.value.action_config.url = url;

  resetApiTestState();
  apiTestLoading.value = true;

  try {
    const method = (form.value.action_config.method || 'POST').toUpperCase();

    let parsedHeaders = {};
    if (form.value.action_config.headers) {
      try {
        parsedHeaders = JSON.parse(form.value.action_config.headers);
      } catch {
        apiTestError.value = t('OMNI.SLOTS.API_CALL.INVALID_HEADERS_JSON');
        apiTestLoading.value = false;
        return;
      }
    }

    const payload = {
      url,
      method,
      headers: parsedHeaders,
    };

    if (['POST', 'PUT', 'PATCH'].includes(method) && form.value.action_config.body) {
      payload.body = form.value.action_config.body;
    }

    payload.timeoutMs = Number(form.value.action_config.timeout_ms) || undefined;
    payload.saveSample = Boolean(props.slot?.id);
    payload.slotId = props.slot?.id;

    const { data: result } = await SlotsConfigAPI.testApiCall(payload);

    apiTestResult.value = {
      status: result.status,
      statusText: result.status_text,
      data: result.data,
    };

    if (result?.data !== undefined) {
      form.value.action_config = {
        ...form.value.action_config,
        test_response_sample: result.data,
        test_response_status: result.status,
      };
    }
  } catch (error) {
    const msg =
      error?.response?.data?.error ||
      error.message ||
      t('OMNI.SLOTS.API_CALL.TEST_ERROR');
    apiTestError.value = msg;
  } finally {
    apiTestLoading.value = false;
  }
};

const formattedApiTestResult = computed(() => {
  if (!apiTestResult.value) return '';
  try {
    const { data } = apiTestResult.value;
    if (typeof data === 'string') return data;
    return JSON.stringify(data, null, 2);
  } catch {
    return String(apiTestResult.value.data);
  }
});

const isCreativeTemperature = computed(() => form.value.ai_temperature === 'creative');
const showQuestionField = computed(() => isAskQuestion.value && !isCreativeTemperature.value);
const showResponseInstructionField = computed(() => isAskQuestion.value && !isCreativeTemperature.value);

// Build the merged field catalog from all slots of the current bot.
// Mirrors what FieldsCatalogService does on the backend: first occurrence per
// field name wins; result sorted alphabetically.
const fieldsCatalog = computed(() => {
  const map = new Map();
  for (const s of props.allSlots) {
    let rj = s.restriction_json;
    if (typeof rj === 'string') {
      try { rj = JSON.parse(rj); } catch { rj = {}; }
    }
    rj = rj || {};
    for (const fd of (rj.fields_descriptions || [])) {
      if (!fd.name || map.has(fd.name)) continue;
      map.set(fd.name, {
        name: fd.name,
        description: fd.description || '',
        possibleValues: fd.possible_values || [],
        sourceSlot: s.slot_name,
      });
    }
  }
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
});

// Fields picker modal state
const showFieldsModal = ref(false);
const restrictionTextareaRef = ref(null);

// Insert a field name at the current cursor position in the restriction textarea,
// then close the modal and restore focus so the user can keep typing.
function insertFieldName(name) {
  const el = restrictionTextareaRef.value;
  const current = form.value.restriction || '';
  const start = el?.selectionStart ?? current.length;
  const end = el?.selectionEnd ?? start;
  form.value.restriction = current.slice(0, start) + name + current.slice(end);
  showFieldsModal.value = false;
  nextTick(() => {
    el?.focus();
    const pos = start + name.length;
    el?.setSelectionRange(pos, pos);
  });
}

// Existing file info from slot
const existingFileName = computed(() => props.slot?.action_file_name || null);
const existingFileSize = computed(() => {
  const size = props.slot?.action_file_size;
  if (!size) return null;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
});

// Insert emoji at cursor position
const onEmojiClick = emoji => {
  const input = questionInput.value;
  if (!input) {
    form.value.question += emoji;
    return;
  }

  const start = input.selectionStart;
  const end = input.selectionEnd;
  const text = form.value.question;

  form.value.question = text.slice(0, start) + emoji + text.slice(end);

  setTimeout(() => {
    input.focus();
    input.setSelectionRange(start + emoji.length, start + emoji.length);
  }, 0);

  showEmojiPicker.value = false;
};

const toggleEmojiPicker = () => {
  showEmojiPicker.value = !showEmojiPicker.value;
};

const hideEmojiPicker = () => {
  showEmojiPicker.value = false;
};

// Get the selected bot name for display
const selectedBotName = computed(() => {
  const botId = form.value.agent_bot_id || props.selectedBotId;
  const bot = props.bots.find(b => b.id === botId);
  return bot?.name || '';
});

// Restriction status from the slot prop
const restrictionStatus = computed(() => props.slot?.restriction_status || 'none');
const restrictionConfidence = computed(() => props.slot?.restriction_confidence || 0);
const restrictionNeedsReview = computed(() => props.slot?.restriction_needs_review || false);
const restrictionAmbiguous = computed(() => props.slot?.restriction_ambiguous || false);
const restrictionValidated = computed(() => props.slot?.restriction_validated || false);
const restrictionNotes = computed(() => props.slot?.restriction_notes || '');
const restrictionErrors = computed(() => props.slot?.restriction_errors || []);
const restrictionJson = computed(() => props.slot?.restriction_json || null);

// Response instruction status from the slot prop
const responseInstructionStatus = computed(() => props.slot?.response_instruction_status || 'none');
const responseInstructionConfidence = computed(() => props.slot?.response_instruction_confidence || 0);
const responseInstructionNeedsReview = computed(() => props.slot?.response_instruction_needs_review || false);
const responseInstructionAmbiguous = computed(() => props.slot?.response_instruction_ambiguous || false);
const responseInstructionValidated = computed(() => props.slot?.response_instruction_validated || false);
const responseInstructionNotes = computed(() => props.slot?.response_instruction_notes || '');
const responseInstructionErrors = computed(() => props.slot?.response_instruction_errors || []);
const responseInstructionJson = computed(() => props.slot?.response_instruction_json || null);

const showRestrictionStatus = computed(() => props.slot?.id && props.slot?.restriction);
const showResponseInstructionStatus = computed(() => props.slot?.id && props.slot?.response_instruction);
const isCompiling = computed(() => ['pending', 'compiling'].includes(restrictionStatus.value));
const isResponseInstructionCompiling = computed(() => ['pending', 'compiling'].includes(responseInstructionStatus.value));
const isFailed = computed(() => restrictionStatus.value === 'error');
const isResponseInstructionFailed = computed(() => responseInstructionStatus.value === 'error');
const confidencePercent = computed(() => Math.round(restrictionConfidence.value * 100));
const responseInstructionConfidencePercent = computed(() => Math.round(responseInstructionConfidence.value * 100));

const tooltipContent = computed(() => {
  const parts = [];
  if (restrictionNotes.value) parts.push(restrictionNotes.value);
  if (restrictionErrors.value.length > 0) {
    parts.push(`${t('OMNI.RESTRICTION_STATUS.ERRORS')}: ${restrictionErrors.value.join(', ')}`);
  }
  if (restrictionConfidence.value > 0) {
    parts.push(`${t('OMNI.RESTRICTION_STATUS.CONFIDENCE')}: ${confidencePercent.value}%`);
  }
  return parts.join('\n');
});

const responseInstructionTooltipContent = computed(() => {
  const parts = [];
  if (responseInstructionNotes.value) parts.push(responseInstructionNotes.value);
  if (responseInstructionErrors.value.length > 0) {
    parts.push(`${t('OMNI.RESPONSE_INSTRUCTION_STATUS.ERRORS')}: ${responseInstructionErrors.value.join(', ')}`);
  }
  if (responseInstructionConfidence.value > 0) {
    parts.push(`${t('OMNI.RESPONSE_INSTRUCTION_STATUS.CONFIDENCE')}: ${responseInstructionConfidencePercent.value}%`);
  }
  return parts.join('\n');
});

const formattedJson = computed(() => {
  if (!restrictionJson.value) return '';
  try { return JSON.stringify(restrictionJson.value, null, 2); } catch { return ''; }
});

const formattedResponseInstructionJson = computed(() => {
  if (!responseInstructionJson.value) return '';
  try { return JSON.stringify(responseInstructionJson.value, null, 2); } catch { return ''; }
});

const showResponseInstructionJsonPreview = ref(false);

const toggleResponseInstructionJsonPreview = () => {
  showResponseInstructionJsonPreview.value = !showResponseInstructionJsonPreview.value;
};

// File handling
const handleFileSelect = event => {
  const file = event.target.files?.[0];
  fileError.value = '';

  if (!file) {
    selectedFile.value = null;
    return;
  }

  if (file.size > MAX_FILE_SIZE) {
    fileError.value = t('OMNI.SLOTS.FILE_SIZE_EXCEEDED');
    selectedFile.value = null;
    return;
  }

  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    fileError.value = t('OMNI.SLOTS.FILE_TYPE_NOT_ALLOWED');
    selectedFile.value = null;
    return;
  }

  selectedFile.value = file;
};

const clearFile = () => {
  selectedFile.value = null;
  fileError.value = '';
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
};

const handleRemoveExistingFile = () => {
  emit('remove-file', props.slot?.id);
};

// Watch for action_type changes - clear irrelevant fields (only on user interaction)
watch(
  () => form.value.action_type,
  (newType, oldType) => {
    if (isResettingForm.value || !oldType || newType === oldType) return;

    // Clear ask_question specific fields when switching away
    if (newType !== 'ask_question') {
      form.value.question = '';
      form.value.response_instruction = '';
    }

    // Reset action_config for the new type
    form.value.action_config = newType === 'api_call' ? { method: 'POST' } : {};

    // Clear file selection and api test state
    selectedFile.value = null;
    fileError.value = '';
    resetApiTestState();
  }
);

// Watch for modal open
watch(
  () => props.isOpen,
  isOpen => {
    if (isOpen) {
      showJsonPreview.value = false;
      showResponseInstructionJsonPreview.value = false;
      showEmojiPicker.value = false;
      selectedFile.value = null;
      fileError.value = '';
      resetApiTestState();
      resetForm();
    }
  }
);

// Watch for slot prop changes (e.g. after compilation via ActionCable)
watch(
  () => props.slot,
  newSlot => {
    if (props.isOpen && newSlot) {
      // Only update compilation status fields, not the form
    }
  },
  { deep: true }
);

const resetForm = () => {
  isResettingForm.value = true;
  if (props.slot) {
    form.value = {
      agent_bot_id: props.slot.agent_bot_id || props.selectedBotId,
      slot_name: props.slot.slot_name || '',
      slot_description: props.slot.slot_description || '',
      question: props.slot.question || '',
      priority: props.slot.priority || '',
      restriction: props.slot.restriction || '',
      response_instruction: props.slot.response_instruction || '',
      action_type: props.slot.action_type || 'ask_question',
      ai_temperature: props.slot.ai_temperature || 'rigid',
      action_config: { ...(props.slot.action_config || {}) },
    };
  } else {
    form.value = {
      agent_bot_id: props.selectedBotId,
      slot_name: '',
      slot_description: '',
      question: '',
      priority: '',
      restriction: '',
      response_instruction: '',
      action_type: 'ask_question',
      ai_temperature: 'rigid',
      action_config: {},
    };
  }
  // nextTick ensures the watcher has fired and been skipped before we re-enable it
  setTimeout(() => {
    isResettingForm.value = false;
  }, 0);
};

const handleSubmit = () => {
  if (form.value.action_type === 'api_call' && form.value.action_config?.url) {
    form.value.action_config.url = normalizeUrl(form.value.action_config.url);
  }
  const data = { ...form.value, expected_type: 'string' };
  emit('save', { data, file: selectedFile.value });
};

const handleClose = () => {
  emit('close');
};

const toggleJsonPreview = () => {
  showJsonPreview.value = !showJsonPreview.value;
};
</script>

<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
  >
    <div
      class="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col transition-[max-width] duration-200"
      :class="modalSizeClass"
    >
      <!-- Header -->
      <div
        class="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between"
      >
        <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {{ slot?.id ? t('OMNI.SLOTS.EDIT_SLOT') : t('OMNI.SLOTS.NEW_SLOT') }}
        </h2>
        <button
          class="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          @click="handleClose"
        >
          <span class="i-lucide-x w-5 h-5" />
        </button>
      </div>

      <!-- Form -->
      <form
        class="flex-1 overflow-y-auto p-6 space-y-4"
        @submit.prevent="handleSubmit"
      >
        <!-- Bot Display (read-only) -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ t('OMNI.SLOTS.BOT') }}
          </label>
          <div
            class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-md bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-300 flex items-center gap-2"
          >
            <span class="i-lucide-bot w-4 h-4" />
            <span>{{ selectedBotName }}</span>
          </div>
          <p class="mt-1 text-xs text-slate-500">
            {{ t('OMNI.SLOTS.BOT_HELP') }}
          </p>
        </div>

        <!-- Slot Name -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ t('OMNI.SLOTS.SLOT_NAME') }} *
          </label>
          <input
            v-model="form.slot_name"
            type="text"
            required
            class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
          />
        </div>

        <!-- Action Type -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ t('OMNI.SLOTS.ACTION_TYPE') }}
          </label>
          <select
            v-model="form.action_type"
            class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
          >
            <option
              v-for="option in actionTypeOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ t(option.label) }}
            </option>
          </select>
          <p class="mt-1 text-xs text-slate-500">
            {{ t('OMNI.SLOTS.ACTION_TYPE_HELP') }}
          </p>
        </div>

        <!-- AI Temperature (hidden for api_call - deterministic slot, no AI reasoning) -->
        <div v-if="!isApiCall">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ t('OMNI.SLOTS.AI_TEMPERATURE') }}
          </label>
          <select
            v-model="form.ai_temperature"
            class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
          >
            <option
              v-for="option in aiTemperatureOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ t(option.label) }}
            </option>
          </select>
          <p class="mt-1 text-xs text-slate-500">
            {{ t('OMNI.SLOTS.AI_TEMPERATURE_HELP') }}
          </p>
        </div>

        <!-- Priority -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ t('OMNI.SLOTS.PRIORITY') }}
          </label>
          <input
            v-model="form.priority"
            type="text"
            class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
          />
        </div>

        <!-- Objective (hidden for api_call - deterministic execution) -->
        <div v-if="!isApiCall">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ t('OMNI.SLOTS.OBJECTIVE') }}
          </label>
          <textarea
            v-model="form.slot_description"
            rows="2"
            class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
          />
        </div>

        <!-- ====== ASK QUESTION FIELDS ====== -->
        <!-- Question (hidden in Creative temperature) -->
        <template v-if="showQuestionField">
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ t('OMNI.SLOTS.QUESTION') }}
            </label>
            <div class="relative">
              <textarea
                ref="questionInput"
                v-model="form.question"
                rows="2"
                class="w-full px-3 py-2 pr-10 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                :placeholder="t('OMNI.SLOTS.QUESTION_PLACEHOLDER')"
              />
              <div
                v-on-click-outside="hideEmojiPicker"
                class="absolute right-2 top-2"
              >
                <button
                  type="button"
                  class="p-1.5 rounded-md transition-colors"
                  :class="
                    showEmojiPicker
                      ? 'text-woot-500 bg-woot-50 dark:bg-woot-900/20'
                      : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600'
                  "
                  title="Emoji"
                  @click="toggleEmojiPicker"
                >
                  <span class="i-lucide-smile-plus w-5 h-5" />
                </button>
                <EmojiInput
                  v-if="showEmojiPicker"
                  class="!absolute !right-0 !top-full mt-1 z-50"
                  :on-click="onEmojiClick"
                />
              </div>
            </div>
          </div>
        </template>

        <!-- Response Instruction (hidden in Creative temperature) -->
        <template v-if="showResponseInstructionField">
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ t('OMNI.SLOTS.RESPONSE_INSTRUCTION') }}
              <span class="text-slate-400 font-normal ml-1">({{ t('OMNI.COMMON.OPTIONAL') }})</span>
            </label>
            <textarea
              v-model="form.response_instruction"
              rows="2"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              :placeholder="t('OMNI.SLOTS.RESPONSE_INSTRUCTION_PLACEHOLDER')"
            />
            <p class="mt-1 text-xs text-slate-500">
              {{ t('OMNI.SLOTS.RESPONSE_INSTRUCTION_HELP') }}
            </p>

            <!-- Response Instruction Status Badge -->
            <div v-if="showResponseInstructionStatus" class="mt-2 flex flex-wrap items-center gap-2">
              <span
                v-if="isResponseInstructionCompiling"
                class="inline-flex items-center gap-1.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2.5 py-1 rounded-full"
              >
                <span class="i-lucide-loader-2 w-3 h-3 animate-spin" />
                {{ t('OMNI.RESPONSE_INSTRUCTION_STATUS.COMPILING') }}
              </span>
              <span
                v-else-if="responseInstructionValidated"
                class="inline-flex items-center gap-1.5 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2.5 py-1 rounded-full"
                :title="responseInstructionTooltipContent"
              >
                <span class="i-lucide-check-circle w-3 h-3" />
                {{ t('OMNI.RESPONSE_INSTRUCTION_STATUS.VALIDATED') }}
                <span class="text-green-600 dark:text-green-400">({{ responseInstructionConfidencePercent }}%)</span>
              </span>
              <span
                v-else-if="responseInstructionAmbiguous"
                class="inline-flex items-center gap-1.5 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 px-2.5 py-1 rounded-full cursor-help"
                :title="responseInstructionTooltipContent"
              >
                <span class="i-lucide-alert-triangle w-3 h-3" />
                {{ t('OMNI.RESPONSE_INSTRUCTION_STATUS.AMBIGUOUS') }}
              </span>
              <span
                v-else-if="isResponseInstructionFailed"
                class="inline-flex items-center gap-1.5 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2.5 py-1 rounded-full cursor-help"
                :title="responseInstructionTooltipContent"
              >
                <span class="i-lucide-x-circle w-3 h-3" />
                {{ t('OMNI.RESPONSE_INSTRUCTION_STATUS.ERROR') }}
              </span>
            </div>

            <div
              v-if="showResponseInstructionStatus && (responseInstructionNotes || responseInstructionErrors.length > 0)"
              class="mt-2 p-2 bg-slate-50 dark:bg-slate-700/50 rounded-md text-xs"
            >
              <p v-if="responseInstructionNotes" class="text-slate-600 dark:text-slate-400">
                <span class="font-medium">{{ t('OMNI.RESPONSE_INSTRUCTION_STATUS.NOTES') }}:</span>
                {{ responseInstructionNotes }}
              </p>
              <div v-if="responseInstructionErrors.length > 0" class="mt-1">
                <span class="font-medium text-red-600 dark:text-red-400">{{ t('OMNI.RESPONSE_INSTRUCTION_STATUS.ERRORS') }}:</span>
                <ul class="list-disc list-inside text-red-600 dark:text-red-400">
                  <li v-for="error in responseInstructionErrors" :key="error">{{ error }}</li>
                </ul>
              </div>
            </div>

            <details
              v-if="responseInstructionJson"
              class="mt-3"
              :open="showResponseInstructionJsonPreview"
              @toggle="toggleResponseInstructionJsonPreview"
            >
              <summary class="text-xs text-slate-500 dark:text-slate-400 cursor-pointer hover:text-slate-700 dark:hover:text-slate-300 select-none">
                {{ t('OMNI.RESPONSE_INSTRUCTION_STATUS.VIEW_COMPILED') }}
              </summary>
              <pre class="mt-2 p-3 bg-slate-100 dark:bg-slate-900 rounded-md text-xs text-slate-700 dark:text-slate-300 overflow-auto max-h-48 font-mono">{{ formattedResponseInstructionJson }}</pre>
            </details>
          </div>

        </template>

        <!-- ====== CHECK CALENDAR FIELDS ====== -->
        <template v-if="isCheckCalendar">
          <!-- Calendar Select -->
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ t('OMNI.SLOTS.SELECT_CALENDAR') }}
            </label>
            <select
              v-model="form.action_config.calendar_id"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            >
              <option value="" disabled>
                {{ t('OMNI.SLOTS.SELECT_CALENDAR_PLACEHOLDER') }}
              </option>
              <option
                v-for="calendar in calendars"
                :key="calendar.id"
                :value="calendar.id"
              >
                {{ calendar.name }}
              </option>
            </select>
            <p
              v-if="calendars.length === 0"
              class="mt-1 text-xs text-yellow-600 dark:text-yellow-400"
            >
              {{ t('OMNI.SLOTS.NO_CALENDARS') }}
            </p>
          </div>

          <!-- AI Instruction -->
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ t('OMNI.SLOTS.AI_INSTRUCTION') }}
            </label>
            <textarea
              v-model="form.action_config.ai_instruction"
              rows="3"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              :placeholder="t('OMNI.SLOTS.AI_INSTRUCTION_PLACEHOLDER')"
            />
            <p class="mt-1 text-xs text-slate-500">
              {{ t('OMNI.SLOTS.AI_INSTRUCTION_HELP') }}
            </p>
          </div>
        </template>

        <!-- ====== QUERY DATABASE FIELDS ====== -->
        <template v-if="isQueryDatabase">
          <!-- AI Instruction -->
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ t('OMNI.SLOTS.AI_INSTRUCTION') }}
            </label>
            <textarea
              v-model="form.action_config.ai_instruction"
              rows="3"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              :placeholder="t('OMNI.SLOTS.AI_INSTRUCTION_PLACEHOLDER')"
            />
            <p class="mt-1 text-xs text-slate-500">
              {{ t('OMNI.SLOTS.AI_INSTRUCTION_HELP') }}
            </p>
          </div>
        </template>

        <!-- ====== SEND FILE FIELDS ====== -->
        <template v-if="isSendFile">
          <!-- File Upload -->
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ t('OMNI.SLOTS.UPLOAD_FILE') }}
            </label>

            <!-- Existing file display -->
            <div
              v-if="existingFileName && !selectedFile"
              class="flex items-center gap-3 p-3 border border-slate-300 dark:border-slate-600 rounded-md bg-slate-50 dark:bg-slate-700"
            >
              <span class="i-lucide-file w-5 h-5 text-slate-500" />
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">
                  {{ existingFileName }}
                </p>
                <p v-if="existingFileSize" class="text-xs text-slate-500">
                  {{ existingFileSize }}
                </p>
              </div>
              <button
                type="button"
                class="text-xs text-woot-500 hover:text-woot-600 font-medium"
                @click="fileInputRef?.click()"
              >
                {{ t('OMNI.SLOTS.CHANGE_FILE') }}
              </button>
              <button
                type="button"
                class="text-xs text-red-500 hover:text-red-600 font-medium"
                @click="handleRemoveExistingFile"
              >
                {{ t('OMNI.SLOTS.REMOVE_FILE') }}
              </button>
            </div>

            <!-- New file selected display -->
            <div
              v-else-if="selectedFile"
              class="flex items-center gap-3 p-3 border border-green-300 dark:border-green-600 rounded-md bg-green-50 dark:bg-green-900/20"
            >
              <span class="i-lucide-file-check w-5 h-5 text-green-600" />
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">
                  {{ selectedFile.name }}
                </p>
                <p class="text-xs text-slate-500">
                  {{ (selectedFile.size / (1024 * 1024)).toFixed(1) }} MB
                </p>
              </div>
              <button
                type="button"
                class="text-xs text-red-500 hover:text-red-600 font-medium"
                @click="clearFile"
              >
                {{ t('OMNI.SLOTS.REMOVE_FILE') }}
              </button>
            </div>

            <!-- File input (when no file) -->
            <div v-else>
              <input
                ref="fileInputRef"
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,video/mp4"
                class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-woot-50 file:text-woot-700 dark:file:bg-woot-900/20 dark:file:text-woot-300 hover:file:bg-woot-100"
                @change="handleFileSelect"
              />
            </div>

            <!-- Hidden file input for change button -->
            <input
              v-if="existingFileName && !selectedFile"
              ref="fileInputRef"
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,video/mp4"
              class="hidden"
              @change="handleFileSelect"
            />

            <!-- Error message -->
            <p v-if="fileError" class="mt-1 text-xs text-red-600 dark:text-red-400">
              {{ fileError }}
            </p>

            <p class="mt-1 text-xs text-slate-500">
              {{ t('OMNI.SLOTS.UPLOAD_FILE_HELP') }}
            </p>
          </div>

          <!-- Caption -->
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ t('OMNI.SLOTS.CAPTION') }}
              <span class="text-slate-400 font-normal ml-1">({{ t('OMNI.COMMON.OPTIONAL') }})</span>
            </label>
            <textarea
              v-model="form.action_config.caption"
              rows="2"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              :placeholder="t('OMNI.SLOTS.CAPTION_PLACEHOLDER')"
            />
          </div>
        </template>

        <!-- ====== DECISION FIELDS ====== -->
        <template v-if="isDecision">
          <div class="flex items-start gap-3 p-3 border border-purple-300 dark:border-purple-600 rounded-md bg-purple-50 dark:bg-purple-900/20">
            <span class="i-lucide-brain w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
            <p class="text-xs text-purple-700 dark:text-purple-300">
              {{ t('OMNI.SLOTS.DECISION_ACTION_HELP') }}
            </p>
          </div>
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ t('OMNI.SLOTS.AI_INSTRUCTION') }}
            </label>
            <textarea
              v-model="form.action_config.ai_instruction"
              rows="3"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              :placeholder="t('OMNI.SLOTS.DECISION_ACTION_PLACEHOLDER')"
            />
            <p class="mt-1 text-xs text-slate-500">
              {{ t('OMNI.SLOTS.AI_INSTRUCTION_HELP') }}
            </p>
          </div>
        </template>

        <!-- ====== FREE FIELDS ====== -->
        <template v-if="isFree">
          <div class="flex items-start gap-3 p-3 border border-amber-300 dark:border-amber-600 rounded-md bg-amber-50 dark:bg-amber-900/20">
            <span class="i-lucide-triangle-alert w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <p class="text-xs text-amber-700 dark:text-amber-300">
              {{ t('OMNI.SLOTS.FREE_ACTION_HELP') }}
            </p>
          </div>
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ t('OMNI.SLOTS.AI_INSTRUCTION') }}
            </label>
            <textarea
              v-model="form.action_config.ai_instruction"
              rows="3"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              :placeholder="t('OMNI.SLOTS.FREE_ACTION_PLACEHOLDER')"
            />
            <p class="mt-1 text-xs text-slate-500">
              {{ t('OMNI.SLOTS.AI_INSTRUCTION_HELP') }}
            </p>
          </div>
        </template>

        <!-- ====== LOOP FIELDS ====== -->
        <template v-if="isLoop">
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ t('OMNI.SLOTS.AI_INSTRUCTION') }}
            </label>
            <textarea
              v-model="form.action_config.ai_instruction"
              rows="3"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              :placeholder="t('OMNI.SLOTS.AI_INSTRUCTION_PLACEHOLDER')"
            />
            <p class="mt-1 text-xs text-slate-500">
              {{ t('OMNI.SLOTS.AI_INSTRUCTION_HELP') }}
            </p>
          </div>
        </template>

        <!-- ====== API CALL FIELDS ====== -->
        <template v-if="isApiCall">
          <!-- HTTP Method -->
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ t('OMNI.SLOTS.API_CALL.METHOD') }}
            </label>
            <select
              v-model="form.action_config.method"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            >
              <option
                v-for="method in apiMethodOptions"
                :key="method"
                :value="method"
              >
                {{ method }}
              </option>
            </select>
          </div>

          <!-- URL -->
          <div>
            <div class="flex items-center justify-between mb-1">
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                {{ t('OMNI.SLOTS.API_CALL.URL') }} *
              </label>
              <TemplatePickersToolbar
                :agent-bot-id="form.agent_bot_id"
                :exclude-slot-id="currentSlotId"
                :all-slots="allSlotsList"
                :api-sample="apiResponseSample"
                @pick="handleUrlPick"
              />
            </div>
            <input
              ref="apiUrlInput"
              v-model="form.action_config.url"
              type="url"
              required
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 font-mono text-sm"
              :placeholder="t('OMNI.SLOTS.API_CALL.URL_PLACEHOLDER')"
            />
          </div>

          <!-- Headers (JSON) -->
          <div>
            <div class="flex items-center justify-between mb-1">
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                {{ t('OMNI.SLOTS.API_CALL.HEADERS') }}
                <span class="text-slate-400 font-normal ml-1">({{ t('OMNI.COMMON.OPTIONAL') }})</span>
              </label>
              <TemplatePickersToolbar
                :agent-bot-id="form.agent_bot_id"
                :exclude-slot-id="currentSlotId"
                :all-slots="allSlotsList"
                :api-sample="apiResponseSample"
                @pick="handleHeadersPick"
              />
            </div>
            <textarea
              ref="apiHeadersInput"
              v-model="form.action_config.headers"
              rows="2"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 font-mono text-sm"
              :placeholder="t('OMNI.SLOTS.API_CALL.HEADERS_PLACEHOLDER')"
            />
          </div>

          <!-- Body (JSON) -->
          <div>
            <div class="flex items-center justify-between mb-1">
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                {{ t('OMNI.SLOTS.API_CALL.BODY') }}
                <span class="text-slate-400 font-normal ml-1">(JSON)</span>
              </label>
              <div class="flex items-center gap-2">
                <TemplatePickersToolbar
                  :agent-bot-id="form.agent_bot_id"
                  :exclude-slot-id="currentSlotId"
                  :all-slots="allSlotsList"
                  :api-sample="apiResponseSample"
                  @pick="handleBodyPick"
                />
                <button
                  type="button"
                  class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-woot-600 dark:text-woot-400 bg-woot-50 dark:bg-woot-900/20 hover:bg-woot-100 dark:hover:bg-woot-900/30 rounded-md transition-colors"
                  @click="insertAiPlaceholder"
                >
                  <span class="i-lucide-sparkles w-3 h-3" />
                  {{ t('OMNI.SLOTS.API_CALL.INSERT_PLACEHOLDER') }}
                </button>
              </div>
            </div>
            <textarea
              ref="apiBodyInput"
              v-model="form.action_config.body"
              rows="4"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 font-mono text-sm"
              :placeholder="t('OMNI.SLOTS.API_CALL.BODY_PLACEHOLDER')"
            />
            <p class="mt-1 text-xs text-slate-500">
              {{ t('OMNI.SLOTS.API_CALL.BODY_HELP') }}
            </p>
          </div>

          <!-- Test Button + Result -->
          <div>
            <button
              type="button"
              class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors"
              :class="
                apiTestLoading
                  ? 'bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-400 cursor-not-allowed'
                  : !form.action_config.url
                    ? 'bg-slate-200 dark:bg-slate-600 text-slate-400 dark:text-slate-500 cursor-not-allowed'
                    : 'bg-woot-500 hover:bg-woot-600 text-white dark:bg-woot-600 dark:hover:bg-woot-500'
              "
              :disabled="apiTestLoading || !form.action_config.url"
              @click="handleApiTest"
            >
              <span
                :class="apiTestLoading ? 'i-lucide-loader-2 w-4 h-4 animate-spin' : 'i-lucide-play w-4 h-4'"
              />
              {{ apiTestLoading ? t('OMNI.SLOTS.API_CALL.TESTING') : t('OMNI.SLOTS.API_CALL.TEST') }}
            </button>

            <!-- Error -->
            <div
              v-if="apiTestError"
              class="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md"
            >
              <div class="flex items-center gap-2 mb-1">
                <span class="i-lucide-x-circle w-4 h-4 text-red-500" />
                <span class="text-sm font-medium text-red-700 dark:text-red-300">
                  {{ t('OMNI.SLOTS.API_CALL.TEST_ERROR') }}
                </span>
              </div>
              <p class="text-xs text-red-600 dark:text-red-400 font-mono">
                {{ apiTestError }}
              </p>
            </div>

            <!-- Result -->
            <div
              v-if="apiTestResult"
              class="mt-3 rounded-md border overflow-hidden"
              :class="
                apiTestResult.status >= 200 && apiTestResult.status < 300
                  ? 'border-green-200 dark:border-green-800'
                  : 'border-yellow-200 dark:border-yellow-800'
              "
            >
              <!-- Status header -->
              <div
                class="flex items-center gap-2 px-3 py-2 text-sm font-medium"
                :class="
                  apiTestResult.status >= 200 && apiTestResult.status < 300
                    ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                    : 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300'
                "
              >
                <span
                  :class="
                    apiTestResult.status >= 200 && apiTestResult.status < 300
                      ? 'i-lucide-check-circle w-4 h-4'
                      : 'i-lucide-alert-triangle w-4 h-4'
                  "
                />
                {{ apiTestResult.status }} {{ apiTestResult.statusText }}
              </div>
              <!-- Response body -->
              <pre
                class="p-3 bg-slate-50 dark:bg-slate-900 text-xs text-slate-700 dark:text-slate-300 overflow-auto max-h-48 font-mono"
              >{{ formattedApiTestResult }}</pre>
            </div>
          </div>

          <!-- Advanced: timeout + retry -->
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Timeout (ms)
              </label>
              <input
                v-model.number="form.action_config.timeout_ms"
                type="number"
                min="500"
                max="60000"
                step="500"
                class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 text-sm"
                placeholder="30000"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Retry on status (CSV)
              </label>
              <input
                v-model="form.action_config.retry_on_status"
                type="text"
                class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 text-sm font-mono"
                placeholder="502, 503, 504"
              />
            </div>
          </div>

          <!-- Automations panel (only after the slot is persisted) -->
          <div
            v-if="currentSlotId"
            class="mt-2 pt-4 border-t border-slate-200 dark:border-slate-700"
          >
            <ApiAutomationsPanel
              :slot-id="currentSlotId"
              :agent-bot-id="form.agent_bot_id"
              :all-slots="allSlotsList"
              :api-sample="apiResponseSample"
            />
          </div>
          <div
            v-else
            class="mt-2 pt-4 border-t border-slate-200 dark:border-slate-700"
          >
            <h4 class="text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              {{ $t('OMNI.SLOTS.API_AUTOMATIONS.TITLE', 'Automações da resposta') }}
            </h4>
            <p class="text-xs text-slate-500 italic">
              Salve o slot para habilitar as automações baseadas na resposta da API.
            </p>
          </div>
        </template>

        <!-- ====== CONSULT CATALOG FIELDS ====== -->
        <template v-if="isConsultCatalog">
          <!-- Catalog Select -->
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ t('OMNI.SLOTS.SELECT_CATALOG') }}
            </label>
            <select
              v-model="form.action_config.catalog_id"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            >
              <option value="" disabled>
                {{ t('OMNI.SLOTS.SELECT_CATALOG_PLACEHOLDER') }}
              </option>
              <option
                v-for="catalog in catalogs"
                :key="catalog.id"
                :value="catalog.id"
              >
                {{ catalog.name }}
              </option>
            </select>
            <p
              v-if="catalogs.length === 0"
              class="mt-1 text-xs text-yellow-600 dark:text-yellow-400"
            >
              {{ t('OMNI.SLOTS.NO_CATALOGS') }}
            </p>
          </div>

          <!-- AI Instruction -->
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ t('OMNI.SLOTS.AI_INSTRUCTION') }}
            </label>
            <textarea
              v-model="form.action_config.ai_instruction"
              rows="3"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              :placeholder="t('OMNI.SLOTS.AI_INSTRUCTION_PLACEHOLDER')"
            />
            <p class="mt-1 text-xs text-slate-500">
              {{ t('OMNI.SLOTS.AI_INSTRUCTION_HELP') }}
            </p>
          </div>
        </template>

        <!-- ====== RESTRICTION (all action types) ====== -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ t('OMNI.SLOTS.RESTRICTION') }}
          </label>
          <textarea
            ref="restrictionTextareaRef"
            v-model="form.restriction"
            rows="2"
            class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            :placeholder="t('OMNI.SLOTS.RESTRICTION_PLACEHOLDER')"
          />
          <div class="mt-1 flex items-center justify-between">
            <p class="text-xs text-slate-500">
              {{ t('OMNI.SLOTS.RESTRICTION_HELP') }}
            </p>
            <button
              type="button"
              class="inline-flex items-center gap-1.5 text-xs font-medium text-woot-600 hover:text-woot-700 dark:text-woot-400 dark:hover:text-woot-300 transition-colors flex-shrink-0 ml-2"
              @click="showFieldsModal = true"
            >
              <span class="i-lucide-list size-3.5" />
              {{ t('OMNI.SLOTS.BROWSE_FIELDS') }}
            </button>
          </div>

          <div v-if="showRestrictionStatus" class="mt-2 flex flex-wrap items-center gap-2">
            <span
              v-if="isCompiling"
              class="inline-flex items-center gap-1.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2.5 py-1 rounded-full"
            >
              <span class="i-lucide-loader-2 w-3 h-3 animate-spin" />
              {{ t('OMNI.RESTRICTION_STATUS.COMPILING') }}
            </span>
            <span
              v-else-if="restrictionValidated"
              class="inline-flex items-center gap-1.5 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2.5 py-1 rounded-full"
              :title="tooltipContent"
            >
              <span class="i-lucide-check-circle w-3 h-3" />
              {{ t('OMNI.RESTRICTION_STATUS.VALIDATED') }}
              <span class="text-green-600 dark:text-green-400">({{ confidencePercent }}%)</span>
            </span>
            <span
              v-else-if="restrictionAmbiguous"
              class="inline-flex items-center gap-1.5 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 px-2.5 py-1 rounded-full cursor-help"
              :title="tooltipContent"
            >
              <span class="i-lucide-alert-triangle w-3 h-3" />
              {{ t('OMNI.RESTRICTION_STATUS.AMBIGUOUS') }}
            </span>
            <span
              v-else-if="isFailed"
              class="inline-flex items-center gap-1.5 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2.5 py-1 rounded-full cursor-help"
              :title="tooltipContent"
            >
              <span class="i-lucide-x-circle w-3 h-3" />
              {{ t('OMNI.RESTRICTION_STATUS.ERROR') }}
            </span>
          </div>

          <div
            v-if="showRestrictionStatus && (restrictionNotes || restrictionErrors.length > 0)"
            class="mt-2 p-2 bg-slate-50 dark:bg-slate-700/50 rounded-md text-xs"
          >
            <p v-if="restrictionNotes" class="text-slate-600 dark:text-slate-400">
              <span class="font-medium">{{ t('OMNI.RESTRICTION_STATUS.NOTES') }}:</span>
              {{ restrictionNotes }}
            </p>
            <div v-if="restrictionErrors.length > 0" class="mt-1">
              <span class="font-medium text-red-600 dark:text-red-400">{{ t('OMNI.RESTRICTION_STATUS.ERRORS') }}:</span>
              <ul class="list-disc list-inside text-red-600 dark:text-red-400">
                <li v-for="error in restrictionErrors" :key="error">{{ error }}</li>
              </ul>
            </div>
          </div>

          <details
            v-if="restrictionJson"
            class="mt-3"
            :open="showJsonPreview"
            @toggle="toggleJsonPreview"
          >
            <summary class="text-xs text-slate-500 dark:text-slate-400 cursor-pointer hover:text-slate-700 dark:hover:text-slate-300 select-none">
              {{ t('OMNI.RESTRICTION_STATUS.VIEW_COMPILED') }}
            </summary>
            <pre class="mt-2 p-3 bg-slate-100 dark:bg-slate-900 rounded-md text-xs text-slate-700 dark:text-slate-300 overflow-auto max-h-48 font-mono">{{ formattedJson }}</pre>
          </details>
        </div>
      </form>

      <!-- Footer -->
      <div
        class="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2"
      >
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md"
          @click="handleClose"
        >
          {{ t('OMNI.COMMON.CANCEL') }}
        </button>
        <button
          type="submit"
          class="px-4 py-2 text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 rounded-md"
          @click="handleSubmit"
        >
          {{ t('OMNI.COMMON.SAVE') }}
        </button>
      </div>
    </div>
  </div>

  <!-- ====== FIELDS PICKER MODAL ====== -->
  <Teleport to="body">
    <div
      v-if="showFieldsModal"
      class="fixed inset-0 z-[9999] flex items-center justify-center"
    >
      <!-- Backdrop -->
      <div
        class="absolute inset-0 bg-black/50 backdrop-blur-sm"
        @click="showFieldsModal = false"
      />

      <!-- Modal -->
      <div class="relative bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col max-h-[80vh]">
        <!-- Header -->
        <div class="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
          <div class="flex items-center gap-2">
            <span class="i-lucide-list text-lg text-woot-500" />
            <h3 class="text-base font-semibold text-slate-900 dark:text-slate-100">
              {{ t('OMNI.SLOTS.FIELDS_MODAL_TITLE') }}
            </h3>
            <span class="text-xs text-slate-500 dark:text-slate-400 ml-1">
              ({{ fieldsCatalog.length }})
            </span>
          </div>
          <button
            type="button"
            class="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            @click="showFieldsModal = false"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <!-- Body -->
        <div class="flex-1 overflow-y-auto">
          <!-- Empty state -->
          <div
            v-if="fieldsCatalog.length === 0"
            class="flex flex-col items-center justify-center py-16 text-center px-6"
          >
            <span class="i-lucide-database text-4xl text-slate-300 dark:text-slate-600 mb-3" />
            <p class="text-sm text-slate-500 dark:text-slate-400">
              {{ t('OMNI.SLOTS.FIELDS_MODAL_EMPTY') }}
            </p>
          </div>

          <!-- Table -->
          <table v-else class="w-full text-sm">
            <thead class="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur-sm">
              <tr>
                <th class="text-left px-5 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide w-5/12">
                  {{ t('OMNI.SLOTS.FIELDS_MODAL_FIELD') }}
                </th>
                <th class="text-left px-5 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide w-4/12">
                  {{ t('OMNI.SLOTS.FIELDS_MODAL_SOURCE_SLOT') }}
                </th>
                <th class="px-5 py-3 w-3/12" />
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
              <tr
                v-for="field in fieldsCatalog"
                :key="field.name"
                class="group hover:bg-woot-50 dark:hover:bg-woot-900/10 transition-colors cursor-pointer"
                @click="insertFieldName(field.name)"
              >
                <td class="px-5 py-3 align-top">
                  <div>
                    <span class="font-mono text-sm font-medium text-slate-800 dark:text-slate-200">
                      {{ field.name }}
                    </span>
                    <p
                      v-if="field.description"
                      class="mt-0.5 text-xs text-slate-500 dark:text-slate-400 leading-relaxed"
                    >
                      {{ field.description }}
                    </p>
                    <div
                      v-if="field.possibleValues && field.possibleValues.length > 0"
                      class="mt-1 flex flex-wrap gap-1"
                    >
                      <span
                        v-for="val in field.possibleValues"
                        :key="val"
                        class="inline-flex px-1.5 py-0.5 text-xs rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 font-mono"
                      >
                        {{ val }}
                      </span>
                    </div>
                  </div>
                </td>
                <td class="px-5 py-3 align-top">
                  <span class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                    {{ field.sourceSlot }}
                  </span>
                </td>
                <td class="px-5 py-3 align-top text-right">
                  <button
                    type="button"
                    class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-woot-50 dark:bg-woot-900/20 text-woot-600 dark:text-woot-400 opacity-0 group-hover:opacity-100 hover:bg-woot-100 dark:hover:bg-woot-900/40 transition-all"
                    @click.stop="insertFieldName(field.name)"
                  >
                    <span class="i-lucide-plus size-3" />
                    {{ t('OMNI.SLOTS.FIELDS_MODAL_SELECT') }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </Teleport>
</template>
